import { Trans } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { ConfirmationDialog } from "../../../components";
import { useLocalization } from "../../../hooks/useLocalization";
import { Routes } from "../../../types/NavigationPaths";
import { useDeleteLicense } from "../hooks/useDeleteLicense";

interface DeleteLicenseDialogProps {
  open: boolean;
  onClose: () => void;
  deletedLicenseName?: string;
  companyName?: string;
  licenseId?: string;
}

export const DeleteLicenseDialog: React.FC<DeleteLicenseDialogProps> = ({
  open,
  onClose,
  deletedLicenseName,
  companyName,
  licenseId,
}) => {
  const { t } = useLocalization();
  const navigate = useNavigate();
  const { companyId } = useParams();

  const onDeleteSuccess = () => {
    onClose();
    void navigate(`/companies/${companyId}${Routes.EXTERNAL_LICENSES}`);
  };

  const deleteLicenseMutation = useDeleteLicense({
    onSuccess: onDeleteSuccess,
  });

  const onDelete = () => {
    deleteLicenseMutation.mutate({ licenseId: licenseId ?? "" });
  };

  return (
    <ConfirmationDialog.Root maxWidth="xs" onClose={onClose} open={open}>
      <ConfirmationDialog.Title iconType="delete" showIcon={true}>
        {t("licenses.deleteTitle")}
      </ConfirmationDialog.Title>
      <ConfirmationDialog.Content error={deleteLicenseMutation.error}>
        <ConfirmationDialog.Text>
          <Trans
            components={{ strong: <strong /> }}
            i18nKey="licenses.deleteConfirmation"
            values={{ deletedLicenseName, companyName }}
          />
        </ConfirmationDialog.Text>
      </ConfirmationDialog.Content>
      <ConfirmationDialog.Actions>
        <ConfirmationDialog.Button onClick={onClose} variant="contained">
          {t("licenses.cancel")}
        </ConfirmationDialog.Button>
        <ConfirmationDialog.Button
          disabled={deleteLicenseMutation.isPending}
          isLoading={deleteLicenseMutation.isPending}
          loadingText={t("licenses.deleting")}
          onClick={onDelete}
          variant="secondary"
        >
          {t("licenses.delete")}
        </ConfirmationDialog.Button>
      </ConfirmationDialog.Actions>
    </ConfirmationDialog.Root>
  );
};
